import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, openRouterApi } from './api';

const AuthContext = createContext(null);

// Translations
const translations = {
  it: {
    welcome: "Benvenuto",
    login: "Accedi",
    register: "Registrati",
    logout: "Esci",
    dashboard: "Dashboard",
    projects: "Progetti",
    settings: "Impostazioni",
    newProject: "Nuovo Progetto",
    totalProjects: "Progetti Totali",
    completed: "Completati",
    totalCost: "Costo Totale",
    recentProjects: "Progetti Recenti",
    noProjects: "Nessun progetto. Crea il tuo primo progetto!",
    enterPrompt: "Descrivi il tuo progetto o richiesta...",
    createProject: "Crea Progetto",
    analyzing: "Analisi in corso...",
    clarifications: "Domande di Chiarimento",
    skip: "Salta",
    submitAnswers: "Invia Risposte",
    configureTeam: "Configura Team",
    teamSize: "Dimensione Team",
    simplified: "Semplificato (2-3 esperti)",
    normal: "Normale (4-7 esperti)",
    extended: "Esteso (8+ esperti)",
    custom: "Personalizzato",
    selectModel: "Seleziona Modello",
    openSourceOnly: "Solo Open Source",
    startExecution: "Avvia Esecuzione",
    automatic: "Automatica",
    stepByStep: "Step-by-Step",
    executing: "Esecuzione in corso...",
    experts: "Esperti",
    synthesis: "Sintesi Finale",
    comment: "Commenta",
    recalculate: "Ricalcola",
    export: "Esporta",
    share: "Condividi",
    versions: "Versioni",
    restoreVersion: "Ripristina Versione",
    estimatedCost: "Costo Stimato",
    apiKeyRequired: "Configura la tua API Key OpenRouter",
    apiKeyPlaceholder: "sk-or-v1-...",
    saveApiKey: "Salva API Key",
    balance: "Saldo",
    confidence: "Confidenza",
    cost: "Costo",
    tokens: "Token",
    time: "Tempo",
    status: "Stato",
    role: "Ruolo",
    model: "Modello",
    pending: "In attesa",
    running: "In esecuzione",
    failed: "Fallito",
    error: "Errore",
    success: "Successo",
    cancel: "Annulla",
    confirm: "Conferma",
    back: "Indietro",
    next: "Avanti",
    save: "Salva",
    delete: "Elimina",
    edit: "Modifica",
    view: "Visualizza",
    close: "Chiudi",
    loading: "Caricamento...",
    noData: "Nessun dato",
    search: "Cerca",
    filter: "Filtra",
    sortBy: "Ordina per",
    price: "Prezzo",
    name: "Nome",
    date: "Data",
    all: "Tutti",
    provider: "Provider",
    criticalIssues: "Criticità Identificate",
    positiveAspects: "Aspetti Positivi",
    recommendations: "Raccomandazioni",
    alternatives: "Alternative",
    nextSteps: "Prossimi Passi",
    contradictions: "Contraddizioni tra Esperti",
  },
  en: {
    welcome: "Welcome",
    login: "Login",
    register: "Register",
    logout: "Logout",
    dashboard: "Dashboard",
    projects: "Projects",
    settings: "Settings",
    newProject: "New Project",
    totalProjects: "Total Projects",
    completed: "Completed",
    totalCost: "Total Cost",
    recentProjects: "Recent Projects",
    noProjects: "No projects yet. Create your first project!",
    enterPrompt: "Describe your project or request...",
    createProject: "Create Project",
    analyzing: "Analyzing...",
    clarifications: "Clarification Questions",
    skip: "Skip",
    submitAnswers: "Submit Answers",
    configureTeam: "Configure Team",
    teamSize: "Team Size",
    simplified: "Simplified (2-3 experts)",
    normal: "Normal (4-7 experts)",
    extended: "Extended (8+ experts)",
    custom: "Custom",
    selectModel: "Select Model",
    openSourceOnly: "Open Source Only",
    startExecution: "Start Execution",
    automatic: "Automatic",
    stepByStep: "Step-by-Step",
    executing: "Executing...",
    experts: "Experts",
    synthesis: "Final Synthesis",
    comment: "Comment",
    recalculate: "Recalculate",
    export: "Export",
    share: "Share",
    versions: "Versions",
    restoreVersion: "Restore Version",
    estimatedCost: "Estimated Cost",
    apiKeyRequired: "Configure your OpenRouter API Key",
    apiKeyPlaceholder: "sk-or-v1-...",
    saveApiKey: "Save API Key",
    balance: "Balance",
    confidence: "Confidence",
    cost: "Cost",
    tokens: "Tokens",
    time: "Time",
    status: "Status",
    role: "Role",
    model: "Model",
    pending: "Pending",
    running: "Running",
    failed: "Failed",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    close: "Close",
    loading: "Loading...",
    noData: "No data",
    search: "Search",
    filter: "Filter",
    sortBy: "Sort by",
    price: "Price",
    name: "Name",
    date: "Date",
    all: "All",
    provider: "Provider",
    criticalIssues: "Critical Issues",
    positiveAspects: "Positive Aspects",
    recommendations: "Recommendations",
    alternatives: "Alternatives",
    nextSteps: "Next Steps",
    contradictions: "Expert Contradictions",
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguageState] = useState(localStorage.getItem('language') || 'it');
  const [hasOpenRouterKey, setHasOpenRouterKey] = useState(false);
  const [openRouterBalance, setOpenRouterBalance] = useState(null);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en'][key] || key;
  }, [language]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await authApi.me();
          setUser(response.data);
          setLanguageState(response.data.language || 'it');
          
          // Check OpenRouter status
          const orStatus = await openRouterApi.status();
          setHasOpenRouterKey(orStatus.data.has_key);
          setOpenRouterBalance(orStatus.data.balance);
        } catch (error) {
          localStorage.clear();
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('tenant_id', user.tenant_id);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    setLanguageState(user.language || 'it');
    
    // Check OpenRouter status
    try {
      const orStatus = await openRouterApi.status();
      setHasOpenRouterKey(orStatus.data.has_key);
      setOpenRouterBalance(orStatus.data.balance);
    } catch (e) {
      // OpenRouter status check may fail for super_admin
    }
    
    return user;
  };

  const register = async (email, password, name) => {
    const response = await authApi.register({ email, password, name });
    
    // Check if registration is pending approval
    if (response.data.status === 'pending') {
      return { status: 'pending', message: response.data.message };
    }
    
    // If approved immediately (first user), proceed with login
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('tenant_id', user.tenant_id);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    setLanguageState(user.language || 'it');
    return { status: 'approved', user };
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setHasOpenRouterKey(false);
    setOpenRouterBalance(null);
  };

  const setLanguage = async (lang) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    if (user) {
      await authApi.updateLanguage(lang);
    }
  };

  const refreshOpenRouterStatus = async () => {
    try {
      const orStatus = await openRouterApi.status();
      setHasOpenRouterKey(orStatus.data.has_key);
      setOpenRouterBalance(orStatus.data.balance);
    } catch (e) {
      console.error('Failed to refresh OpenRouter status:', e);
    }
  };

  const isAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      language,
      setLanguage,
      t,
      hasOpenRouterKey,
      openRouterBalance,
      refreshOpenRouterStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
